// src/components/reporting/cohort/CohortSummaryPage.js
// Cohort Real Summary — aggregated REAL-framework scores across a cohort's
// qualifying members, with a single narrative pass. See
// documentation/Reporting-Analytics/FrontendSpec-CohortAnalysisTrackA.md.
//
//   status  GET  /api/v1/analytics/cohort-real-summary/status?cohortId=&targetTenantId=
//   create  POST /api/v1/analytics/cohort-real-summary            { cohort_id, targetTenantId }
//   fetch   GET  /api/v1/analytics/cohort-real-summary/:reportId
//
// State machine: 'loading' | 'access-denied' | 'gate' | 'generating' |
// 'loading-report' | 'report' | 'error'. 'gate' is skipped entirely for
// state CURRENT — the status fetch routes straight into 'loading-report'
// for existingReport.id, per spec §2/§3 ("no gate at all").
//
// Polling reuses the shared useReportPolling hook (the same infrastructure
// AdHocAnalysis/AssessmentView/RoleplayView use) rather than a page-local
// interval, but with a schedule tuned to this spec's requirements — flat 3s
// cadence, and a totalTimeoutMs long enough that the hook's built-in
// "give up and report timed_out" behavior never practically fires: the spec
// is explicit that the frontend must keep polling indefinitely and let the
// backend own failure detection, not time out client-side.
import { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { XCircle, WarningCircle, PrinterIcon } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import apiService from '../../../services/apiService';
import { useReportPolling } from '../../../hooks/useReportPolling';
import { useConversationRefresh } from '../../../hooks/useConversationRefresh';
import EditableTitle from '../../common/EditableTitle';
import CohortChatPanel from './CohortChatPanel';
import '../../../styles/cohortSummary.css';

const POLL_SCHEDULE = {
  initialDelayMs:   3_000,
  phase1DurationMs: 24 * 60 * 60 * 1000, // effectively "always phase 1" — flat cadence
  phase1IntervalMs: 3_000,
  phase2IntervalMs: 3_000,
  totalTimeoutMs:   24 * 60 * 60 * 1000, // long enough the hook's timeout never fires in practice
};

const SLOW_MESSAGE_AFTER_MS = 90_000;

// Resizable chat/report split — same pattern as AssessmentView's
// conversation/artifact split (splitPct + drag handle).
const MIN_CHAT_PCT = 30;
const MAX_CHAT_PCT = 75;
const DEFAULT_CHAT_PCT = 60;

const formatDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

const STAGES = [
  { key: 'ready',  label: 'Ready'  },
  { key: 'engage', label: 'Engage' },
  { key: 'agree',  label: 'Agree'  },
  { key: 'lead',   label: 'Lead'   },
];

const DRIVERS = [
  { key: 'control',     label: 'Control'     },
  { key: 'comfort',     label: 'Comfort'     },
  { key: 'competition', label: 'Competition' },
  { key: 'condemnation', label: 'Condemnation' },
];

// Display labels only — the canonical field names come from
// cohortRealSummaryDictionary.js's SUB_SCORES (shared with the backend).
const SUB_SCORE_LABELS = {
  issue_framing: 'Issue framing',
  evidence_quality: 'Evidence quality',
  stakes_modeling: 'Stakes modeling',
  challenge_accuracy: 'Challenge accuracy',
  frame_quality: 'Frame quality',
  express_effectiveness: 'Express effectiveness',
  explore_depth: 'Explore depth',
  listen_quality: 'Listen quality',
  agreement_authenticity: 'Agreement authenticity',
  action_specificity: 'Action specificity',
};

const PITFALL_TYPE_LABEL = { trap: 'Trap', gap: 'Gap' };

// Counts here are sometimes the string '<3' (small-cell suppression from
// aggregateScores) rather than a number — render either as-is.
function formatCount(value) {
  return value === undefined || value === null ? '0' : String(value);
}

// Shared by every "count of participants per bucket" section (driver
// dominance, breakdown stage, driver signals, furthest authentic stage) —
// same visual treatment as the original overprotective-program bars.
function CountBarList({ items }) {
  const maxCount = Math.max(1, ...items.map((i) => (typeof i.count === 'number' ? i.count : 0)));
  return (
    <div className="cs-driver-list">
      {items.map(({ key, label, count }) => (
        <div key={key} className="cs-driver-row">
          <span className="cs-driver-label">{label}</span>
          <div className="cs-driver-track">
            <div
              className="cs-driver-fill"
              style={{ width: `${((typeof count === 'number' ? count : 0) / maxCount) * 100}%` }}
            />
          </div>
          <span className="cs-driver-value">{formatCount(count)}</span>
        </div>
      ))}
    </div>
  );
}

CountBarList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })).isRequired,
};

// ---------------------------------------------------------------------------
// StatusGate panels
// ---------------------------------------------------------------------------

function BelowThresholdPanel({ coverage, existingReport, onViewExisting }) {
  return (
    <div className="cs-gate-panel cs-gate-danger">
      <div className="cs-gate-icon"><XCircle size={28} weight="fill" /></div>
      <div className="cs-gate-body">
        <h2 className="cs-gate-title">Not enough data yet</h2>
        <p className="cs-gate-text">
          You need at least 6 completed case studies to generate a cohort summary —
          currently {coverage.qualifyingUserCount} of {coverage.totalCohortMembers}.
        </p>
        {existingReport && (
          <button type="button" className="arp-btn arp-btn-secondary" onClick={onViewExisting}>
            View current report
          </button>
        )}
      </div>
    </div>
  );
}

function NoReportYetPanel({ coverage, onCreate }) {
  return (
    <div className="cs-gate-panel cs-gate-neutral">
      <div className="cs-gate-body">
        <h2 className="cs-gate-title">Ready to generate</h2>
        <p className="cs-gate-text">
          {coverage.qualifyingUserCount} of {coverage.totalCohortMembers} participants have completed their case study.
        </p>
        <button type="button" className="arp-btn arp-btn-primary" onClick={onCreate}>
          Create Cohort Summary
        </button>
      </div>
    </div>
  );
}

function StaleReportPanel({ onUpdate, onViewExisting }) {
  return (
    <div className="cs-gate-panel cs-gate-warning">
      <div className="cs-gate-icon"><WarningCircle size={28} weight="fill" /></div>
      <div className="cs-gate-body">
        <h2 className="cs-gate-title">This summary is out of date</h2>
        <p className="cs-gate-text">
          New case studies have been completed since this summary was last generated.
        </p>
        <div className="cs-gate-actions">
          <button type="button" className="arp-btn arp-btn-primary" onClick={onUpdate}>
            Update Cohort Summary
          </button>
          <button type="button" className="arp-btn arp-btn-secondary" onClick={onViewExisting}>
            View Current Report
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusGate({ statusData, onCreate, onUpdate, onViewExisting }) {
  const { state, coverage, existingReport } = statusData;

  if (state === 'BELOW_THRESHOLD') {
    return (
      <BelowThresholdPanel
        coverage={coverage}
        existingReport={existingReport}
        onViewExisting={() => onViewExisting(existingReport.id)}
      />
    );
  }
  if (state === 'STALE') {
    return (
      <StaleReportPanel
        onUpdate={onUpdate}
        onViewExisting={() => onViewExisting(existingReport.id)}
      />
    );
  }
  // NO_REPORT_YET
  return <NoReportYetPanel coverage={coverage} onCreate={onCreate} />;
}

// ---------------------------------------------------------------------------
// GeneratingOverlay
// ---------------------------------------------------------------------------

function GeneratingOverlay({ estimatedTime }) {
  const [showSlowMessage, setShowSlowMessage] = useState(false);

  useEffect(() => {
    setShowSlowMessage(false);
    const timer = setTimeout(() => setShowSlowMessage(true), SLOW_MESSAGE_AFTER_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="cs-generating">
      <div className="loading-dots"><div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" /></div>
      <p className="cs-generating-text">Generating cohort summary…</p>
      {estimatedTime && !showSlowMessage && (
        <p className="cs-generating-meta">Usually takes {estimatedTime}.</p>
      )}
      {showSlowMessage && (
        <p className="cs-generating-meta cs-generating-slow">
          This is taking longer than usual, still working…
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReportView
// ---------------------------------------------------------------------------

function SubScoreRow({ dimension, stats }) {
  if (!stats) return null;
  return (
    <div className="cs-subscore-row">
      <span className="cs-subscore-label">{SUB_SCORE_LABELS[dimension] || dimension}</span>
      <div className="cs-subscore-track">
        <div className="cs-subscore-fill" style={{ width: `${(stats.avg / 5) * 100}%` }} />
      </div>
      <span className="cs-subscore-value">{stats.avg.toFixed(1)}</span>
    </div>
  );
}

SubScoreRow.propTypes = {
  dimension: PropTypes.string.isRequired,
  stats: PropTypes.shape({ avg: PropTypes.number }),
};

function StageDistribution({ stageDistribution, subScores }) {
  return (
    <div className="cs-detail-section">
      <h3 className="cs-detail-heading">Stage performance across the cohort</h3>
      <div className="cs-stage-grid">
        {STAGES.map(({ key, label }) => {
          const stage = stageDistribution?.[key];
          if (!stage) return null;
          const total = stage.H + stage.M + stage.L || 1;
          const stageSubScores = subScores?.[key];
          return (
            <div key={key} className="cs-stage-card">
              <div className="cs-stage-card-header">
                <span className="cs-stage-label">{label}</span>
                <span className="cs-stage-avg">{stage.avgComposite.toFixed(1)} avg</span>
              </div>
              <div className="cs-stacked-bar">
                <div className="cs-bar-segment cs-bar-high" style={{ width: `${(stage.H / total) * 100}%` }} />
                <div className="cs-bar-segment cs-bar-mid" style={{ width: `${(stage.M / total) * 100}%` }} />
                <div className="cs-bar-segment cs-bar-low" style={{ width: `${(stage.L / total) * 100}%` }} />
              </div>
              <div className="cs-stage-counts">
                <span><span className="cs-dot cs-dot-high" />High {stage.H}</span>
                <span><span className="cs-dot cs-dot-mid" />Med {stage.M}</span>
                <span><span className="cs-dot cs-dot-low" />Low {stage.L}</span>
              </div>
              {stageSubScores && (
                <div className="cs-subscore-list">
                  {Object.entries(stageSubScores).map(([dimension, stats]) => (
                    <SubScoreRow key={dimension} dimension={dimension} stats={stats} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SayThinkGap({ sayThinkGapSeverity }) {
  if (!sayThinkGapSeverity) return null;
  const { avg, distribution } = sayThinkGapSeverity;
  const maxCount = Math.max(1, ...Object.values(distribution));
  return (
    <div className="cs-detail-section">
      <h3 className="cs-detail-heading">Say/think gap severity histogram</h3>
      <div className="cs-gap-summary">
        <span className="cs-gap-avg">{avg.toFixed(1)}</span>
        <span className="cs-gap-avg-label">average (1–5)</span>
      </div>
      <div className="cs-gap-distribution">
        {[1, 2, 3, 4, 5].map((n) => (
          <div key={n} className="cs-gap-bar-col">
            <div className="cs-gap-bar-track">
              <div
                className="cs-gap-bar-fill"
                style={{ height: `${(((distribution[n] || 0)) / maxCount) * 100}%` }}
              />
            </div>
            <span className="cs-gap-bar-label">{n}</span>
            <span className="cs-gap-bar-count">{distribution[n] || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function OverprotectiveProgram({ overprotectiveProgram }) {
  if (!overprotectiveProgram) return null;
  const maxAvg = Math.max(1, ...DRIVERS.map((d) => overprotectiveProgram[d.key]?.avg || 0));
  return (
    <div className="cs-detail-section">
      <h3 className="cs-detail-heading">What drives holding back (overprotective program cohort averages)</h3>
      <div className="cs-driver-list">
        {DRIVERS.map(({ key, label }) => {
          const avg = overprotectiveProgram[key]?.avg;
          if (avg === undefined) return null;
          return (
            <div key={key} className="cs-driver-row">
              <span className="cs-driver-label">{label}</span>
              <div className="cs-driver-track">
                <div className="cs-driver-fill" style={{ width: `${(avg / maxAvg) * 100}%` }} />
              </div>
              <span className="cs-driver-value">{avg.toFixed(1)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Coverage note — sample size context, shown above the numbers so a reader
// knows how much weight to give a finding.
// ---------------------------------------------------------------------------

function CoverageNote({ coverage }) {
  if (!coverage) return null;
  const { qualifyingUserCount, totalCohortMembers, pass2Available } = coverage;
  let text = Number.isInteger(totalCohortMembers)
    ? `${qualifyingUserCount} of ${totalCohortMembers} cohort members are included in this report.`
    : `${qualifyingUserCount} participant${qualifyingUserCount === 1 ? '' : 's'} are included in this report.`;
  if (Number.isInteger(pass2Available) && pass2Available < qualifyingUserCount) {
    text += ` The detailed exchange-level analysis below is available for ${pass2Available} of them.`;
  }
  return <p className="cs-coverage-note">{text}</p>;
}

CoverageNote.propTypes = {
  coverage: PropTypes.shape({
    qualifyingUserCount: PropTypes.number,
    totalCohortMembers: PropTypes.number,
    pass2Available: PropTypes.number,
  }),
};

// ---------------------------------------------------------------------------
// Driver dominance — how many participants were most driven by each of the
// Overprotective Program's four drivers (plus tied/none), vs.
// OverprotectiveProgram above which shows average severity, not headcount.
// ---------------------------------------------------------------------------

const DOMINANCE_EXTRA = [
  { key: 'tied', label: 'Tied' },
  { key: 'none', label: 'None' },
];

function DriverDominance({ driverProfile }) {
  if (!driverProfile?.dominant) return null;
  const { dominant, minDominantScore } = driverProfile;
  const items = [...DRIVERS, ...DOMINANCE_EXTRA].map(({ key, label }) => ({
    key, label, count: dominant[key] ?? 0,
  }));
  return (
    <div className="cs-detail-section">
      <h3 className="cs-detail-heading">Which driver dominated each participant&apos;s conversation</h3>
      <p className="cs-detail-subtext">
        Counts participants by their single strongest driver (a score of {minDominantScore} or higher required).
      </p>
      <CountBarList items={items} />
    </div>
  );
}

DriverDominance.propTypes = {
  driverProfile: PropTypes.shape({
    dominant: PropTypes.object,
    minDominantScore: PropTypes.number,
  }),
};

// ---------------------------------------------------------------------------
// Breakdown stage — where conversations first went off track.
// ---------------------------------------------------------------------------

function BreakdownStage({ breakdownStage }) {
  if (!breakdownStage) return null;
  const items = STAGES.map(({ key, label }) => ({ key, label, count: breakdownStage[key] ?? 0 }));
  return (
    <div className="cs-detail-section">
      <h3 className="cs-detail-heading">Where conversations first went off track</h3>
      <CountBarList items={items} />
    </div>
  );
}

BreakdownStage.propTypes = {
  breakdownStage: PropTypes.object,
};

// ---------------------------------------------------------------------------
// Conversation dynamics — the detailed (Pass 2) exchange-level analysis:
// traps/gaps ranked by prevalence, plus the secondary signals. Only
// available for the subset of participants covered by coverage.pass2Available.
// ---------------------------------------------------------------------------

function PitfallRow({ pitfall }) {
  return (
    <div className="cs-pitfall-row">
      <span className={`cs-pitfall-type cs-pitfall-${pitfall.type}`}>
        {PITFALL_TYPE_LABEL[pitfall.type] || pitfall.type}
      </span>
      <span className="cs-pitfall-name">{pitfall.name}</span>
      <span className="cs-pitfall-count">{formatCount(pitfall.participants)} participants</span>
      <span className="cs-pitfall-high">{formatCount(pitfall.highSeverity)} high severity</span>
    </div>
  );
}

PitfallRow.propTypes = {
  pitfall: PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    participants: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    highSeverity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
};

function ConversationDynamics({ conversationDynamics, pass2Available }) {
  if (!conversationDynamics) {
    return (
      <div className="cs-detail-section">
        <h3 className="cs-detail-heading">Traps and gaps in the conversations</h3>
        <p className="cs-detail-text-muted">
          The detailed exchange-level analysis isn&apos;t available for this report.
        </p>
      </div>
    );
  }

  const { pitfalls, furthestAuthenticStage, prematureTransition, surfaceCompliance, driverSignals } = conversationDynamics;

  return (
    <div className="cs-detail-section">
      <h3 className="cs-detail-heading">Traps and gaps in the conversations</h3>
      {Number.isInteger(pass2Available) && (
        <p className="cs-detail-subtext">
          Out of {pass2Available} participant{pass2Available === 1 ? '' : 's'} with detailed analysis, most common first.
        </p>
      )}

      {pitfalls?.length > 0 && (
        <div className="cs-pitfall-list">
          {pitfalls.map((p) => <PitfallRow key={p.name} pitfall={p} />)}
        </div>
      )}

      <div className="cs-dynamics-signals">
        <div className="cs-dynamics-signal">
          <span className="cs-dynamics-signal-label">Moved on before the earlier stage was settled</span>
          <span className="cs-dynamics-signal-value">{formatCount(prematureTransition)}</span>
        </div>
        <div className="cs-dynamics-signal">
          <span className="cs-dynamics-signal-label">Counterpart&apos;s agreement looked surface-level</span>
          <span className="cs-dynamics-signal-value">{formatCount(surfaceCompliance)}</span>
        </div>
      </div>

      {driverSignals && (
        <div className="cs-detail-section-nested">
          <h4 className="cs-detail-subheading">Driver traced to a specific exchange</h4>
          <CountBarList items={DRIVERS.map(({ key, label }) => ({ key, label, count: driverSignals[key] }))} />
        </div>
      )}

      {furthestAuthenticStage && (
        <div className="cs-detail-section-nested">
          <h4 className="cs-detail-subheading">Furthest stage genuinely reached</h4>
          <CountBarList items={STAGES.map(({ key, label }) => ({ key, label, count: furthestAuthenticStage[key] ?? 0 }))} />
        </div>
      )}
    </div>
  );
}

ConversationDynamics.propTypes = {
  conversationDynamics: PropTypes.shape({
    pitfalls: PropTypes.array,
    furthestAuthenticStage: PropTypes.object,
    prematureTransition: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    surfaceCompliance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    driverSignals: PropTypes.object,
  }),
  pass2Available: PropTypes.number,
};

function ReportView({ report, cohortName }) {
  const result = report.result || {};
  return (
    <div className="cs-report">
      <div className="cs-report-header">
        <h2 className="cs-report-title">Cohort: {cohortName || 'Unknown'} — {formatDate(report.createdAt)}</h2>
        <button
          type="button"
          className="rp-download-btn"
          onClick={() => window.print()}
          title="Print / save as PDF"
        >
          <PrinterIcon size={18} />
        </button>
      </div>

      {result.narrative && (
        <div className="cs-narrative">
          {result.narrative.split('\n\n').filter(Boolean).map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      )}

      <CoverageNote coverage={result.coverage} />

      <div className="cs-detail-divider" />
      <p className="cs-detail-intro">The numbers behind this summary</p>

      <div className="cs-detail-grid">
        <ConversationDynamics
          conversationDynamics={result.conversationDynamics}
          pass2Available={result.coverage?.pass2Available}
        />
        <DriverDominance driverProfile={result.driverProfile} />
        <StageDistribution stageDistribution={result.stageDistribution} subScores={result.subScores} />
        <SayThinkGap sayThinkGapSeverity={result.sayThinkGapSeverity} />
        <OverprotectiveProgram overprotectiveProgram={result.overprotectiveProgram} />
        <BreakdownStage breakdownStage={result.breakdownStage} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WorkspaceTabs — generic tab container for the right-hand panel. One tab
// ("Report") ships today; adding a second flavor's tab later is one more
// entry in the `tabs` array passed in, not a restructure.
// ---------------------------------------------------------------------------

function WorkspaceTabs({ tabs, activeTab, onTabChange }) {
  const active = tabs.find((t) => t.key === activeTab) || tabs[0];
  return (
    <div className="cs-tab-container">
      <div className="cs-tab-strip" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={tab.key === active.key}
            className={`cs-tab ${tab.key === active.key ? 'active' : ''}`}
            onClick={() => onTabChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="cs-tab-content">
        {active.content}
      </div>
    </div>
  );
}

WorkspaceTabs.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    content: PropTypes.node,
  })).isRequired,
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const CohortSummaryPage = ({ cohortId, cohortName, targetTenantId, breadcrumb }) => {
  const { user } = useAuth();
  const { triggerRefresh } = useConversationRefresh();

  const [pageState, setPageState] = useState('loading');
  const [statusData, setStatusData] = useState(null);
  const [activeReportId, setActiveReportId] = useState(null);
  const [estimatedTime, setEstimatedTime] = useState(null);
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState('report');
  const [splitPct, setSplitPct] = useState(DEFAULT_CHAT_PCT);
  const [conversationId, setConversationId] = useState(null);
  const [conversationTitle, setConversationTitle] = useState(null);
  const stateRef = useRef(pageState);
  stateRef.current = pageState;
  const workspaceRef = useRef(null);
  const isDragging = useRef(false);

  // Fired once by CohortChatPanel when it resolves (creates or resumes) the
  // conversation — lets the header show/edit the real conversation title
  // instead of the static "Cohort Real Summary" label.
  const handleConversationResolved = useCallback((id, title) => {
    setConversationId(id);
    setConversationTitle(title);
  }, []);

  const handleTitleUpdate = useCallback(async (newTitle) => {
    try {
      await apiService.put(`/api/v1/conversations/${conversationId}`, { title: newTitle }, user);
      setConversationTitle(newTitle);
      triggerRefresh();
    } catch (err) {
      console.error('[CohortSummaryPage] Failed to update conversation title:', err);
    }
  }, [conversationId, user, triggerRefresh]);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;

    const onMouseMove = (moveEvent) => {
      if (!isDragging.current || !workspaceRef.current) return;
      const rect = workspaceRef.current.getBoundingClientRect();
      const pct = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      setSplitPct(Math.min(Math.max(pct, MIN_CHAT_PCT), MAX_CHAT_PCT));
    };

    const onMouseUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const openReport = useCallback(async (reportId) => {
    setPageState('loading-report');
    try {
      // tenantGuardMiddleware runs on every route in this router, including
      // GET /:reportId — targetTenantId must be a query param here too, not
      // just on the /status call.
      const params = new URLSearchParams({ targetTenantId: String(targetTenantId) });
      const resp = await apiService.get(`/api/v1/analytics/cohort-real-summary/${reportId}?${params}`, user);
      if (!resp.success || !resp.data) {
        setPageState('error');
        return;
      }
      if (resp.data.status === 'completed') {
        setReport(resp.data);
        setPageState('report');
      } else if (resp.data.status === 'failed') {
        setPageState('error');
      } else {
        // Pending/processing (a regeneration is already in flight) — fall
        // into the same waiting screen and poll loop as a fresh generation.
        setActiveReportId(reportId);
        setPageState('generating');
      }
    } catch (err) {
      console.error('[CohortSummaryPage] Failed to load report:', err);
      setPageState('error');
    }
  }, [user, targetTenantId]);

  const loadStatus = useCallback(async () => {
    setPageState('loading');
    try {
      const params = new URLSearchParams({ cohortId, targetTenantId: String(targetTenantId) });
      const resp = await apiService.get(`/api/v1/analytics/cohort-real-summary/status?${params}`, user);
      if (!resp.success || !resp.data) {
        setPageState('error');
        return;
      }
      setStatusData(resp.data);
      if (resp.data.state === 'CURRENT') {
        await openReport(resp.data.existingReport.id);
      } else {
        setPageState('gate');
      }
    } catch (err) {
      if (err?.status === 403 || err?.status === 404) {
        setPageState('access-denied');
        return;
      }
      console.error('[CohortSummaryPage] Failed to load status:', err);
      setPageState('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortId, targetTenantId, openReport]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleCreateOrUpdate = useCallback(async () => {
    try {
      const resp = await apiService.post(
        '/api/v1/analytics/cohort-real-summary',
        { cohort_id: cohortId, targetTenantId },
        user
      );
      if (resp.success && resp.data?.reportId) {
        setEstimatedTime(resp.data.estimatedTime || null);
        setActiveReportId(resp.data.reportId);
        setPageState('generating');
      } else {
        setPageState('error');
      }
    } catch (err) {
      if (err?.code === 'THRESHOLD_NOT_MET') {
        // Cohort membership shifted between the status fetch and the click —
        // re-fetch and re-render whatever state comes back, no special copy.
        await loadStatus();
        return;
      }
      console.error('[CohortSummaryPage] Failed to start generation:', err);
      setPageState('error');
    }
  }, [cohortId, targetTenantId, user, loadStatus]);

  // Polling adapter for useReportPolling — must never reject, and returns an
  // object carrying `status` so the hook knows when to stop. targetTenantId
  // is required here for the same reason as openReport above.
  const fetchGeneratingStatus = useCallback(async (signal) => {
    try {
      const params = new URLSearchParams({ targetTenantId: String(targetTenantId) });
      const resp = await apiService.get(`/api/v1/analytics/cohort-real-summary/${activeReportId}?${params}`, user, { signal });
      if (!resp?.success || !resp.data) return null;
      return resp.data;
    } catch (err) {
      if (err?.name === 'AbortError') return null;
      // A 4xx here is a real, non-recoverable problem (bad tenant, revoked
      // access, the report vanished) — surface it as failed rather than
      // retrying forever on a request that will never succeed. Only
      // network/5xx errors are treated as transient.
      if (err?.status >= 400 && err?.status < 500) {
        console.error('[CohortSummaryPage] Poll failed with a client error, stopping:', err);
        return { status: 'failed' };
      }
      console.error('[CohortSummaryPage] Poll failed, retrying:', err);
      return null; // transient — useReportPolling retries on schedule
    }
  }, [activeReportId, targetTenantId, user]);

  const handlePollUpdate = useCallback((polled) => {
    if (polled.status === 'completed') {
      setReport(polled);
      setPageState('report');
    } else if (polled.status === 'failed') {
      setPageState('error');
    }
    // 'timed_out' is not surfaced as an error per spec — the hook's timeout
    // is set long enough it should never fire in practice; if it somehow
    // does, staying on the generating screen (rather than erroring) matches
    // the spec's "keep polling indefinitely" intent as closely as a bounded
    // hook allows.
  }, []);

  useReportPolling({
    active: pageState === 'generating' && !!activeReportId,
    pollKey: activeReportId,
    fetchStatus: fetchGeneratingStatus,
    onUpdate: handlePollUpdate,
    schedule: POLL_SCHEDULE,
  });

  return (
    <div className="cs-page">
      {breadcrumb}
      <div className="list-header cs-header">
        <div className="cs-header-left">
          {conversationId ? (
            <EditableTitle
              title={conversationTitle}
              conversationId={conversationId}
              onUpdate={handleTitleUpdate}
            />
          ) : (
            <h1 className="cs-static-title">Cohort Real Summary</h1>
          )}
        </div>
      </div>

      {pageState === 'loading' && <div className="arp-state">Loading…</div>}

      {pageState === 'loading-report' && <div className="arp-state">Loading report…</div>}

      {pageState === 'access-denied' && (
        <div className="arp-error-state">You don&apos;t have access to this cohort&apos;s summary.</div>
      )}

      {pageState === 'gate' && statusData && (
        <StatusGate
          statusData={statusData}
          onCreate={handleCreateOrUpdate}
          onUpdate={handleCreateOrUpdate}
          onViewExisting={openReport}
        />
      )}

      {pageState === 'generating' && <GeneratingOverlay estimatedTime={estimatedTime} />}

      {pageState === 'report' && report && (
        <div className="cs-workspace" ref={workspaceRef}>
          <div className="cs-chat-panel" style={{ flexBasis: `${splitPct}%` }}>
            <CohortChatPanel cohortId={cohortId} onConversationResolved={handleConversationResolved} />
          </div>

          <div className="cs-resize-handle" onMouseDown={handleResizeStart} />

          <div className="cs-tab-panel" style={{ flexBasis: `${100 - splitPct}%` }}>
            <WorkspaceTabs
              tabs={[
                { key: 'report', label: 'Report', content: <ReportView report={report} cohortName={cohortName} /> },
              ]}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>
        </div>
      )}

      {pageState === 'error' && (
        <div className="cs-error-panel">
          <p className="arp-error-state">This report couldn&apos;t be generated — try again.</p>
          <button type="button" className="arp-btn arp-btn-primary" onClick={loadStatus}>
            Try Again
          </button>
        </div>
      )}
    </div>
  );
};

BelowThresholdPanel.propTypes = {
  coverage: PropTypes.object.isRequired,
  existingReport: PropTypes.object,
  onViewExisting: PropTypes.func.isRequired,
};
NoReportYetPanel.propTypes = {
  coverage: PropTypes.object.isRequired,
  onCreate: PropTypes.func.isRequired,
};
StaleReportPanel.propTypes = {
  onUpdate: PropTypes.func.isRequired,
  onViewExisting: PropTypes.func.isRequired,
};
StatusGate.propTypes = {
  statusData: PropTypes.object.isRequired,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onViewExisting: PropTypes.func.isRequired,
};
GeneratingOverlay.propTypes = {
  estimatedTime: PropTypes.string,
};
StageDistribution.propTypes = { stageDistribution: PropTypes.object, subScores: PropTypes.object };
SayThinkGap.propTypes = { sayThinkGapSeverity: PropTypes.object };
OverprotectiveProgram.propTypes = { overprotectiveProgram: PropTypes.object };
ReportView.propTypes = { report: PropTypes.object.isRequired, cohortName: PropTypes.string };

CohortSummaryPage.propTypes = {
  cohortId: PropTypes.string.isRequired,
  cohortName: PropTypes.string,
  targetTenantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  breadcrumb: PropTypes.node,
};

export default CohortSummaryPage;
